import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Bell,
  CheckCircle2,
  ChevronRight,
  ClipboardPaste,
  Gavel,
  Landmark,
  Link2,
  ShieldAlert,
  ShieldCheck,
  Ticket,
  X,
} from 'lucide-react';

/**
 * Loan Auction Workbench — the LL-native construction surface
 * (ll-construction-surface-redesign.md, Decisions 1–3 + 5).
 *
 * Three synchronized zones: auction rail | list detail | action dock. Every
 * state transition renders in the dock beside the line it concerns. All flows
 * are the governed machine proven in BWIC_CONSTRUCTION_E2E_0814.md — this
 * surface changes presentation only.
 */

// ── Types (API view shapes) ─────────────────────────────────────────────────

interface MarketListItem {
  id: number;
  securityDescription?: string;
  securityIdentifier?: string;
  faceAmount?: number;
  talkPriceLow?: number;
  talkPriceHigh?: number;
  resolutionStatus?: string | null;
  ourBidPrice?: number;
  ourBidSize?: number;
  weWon?: boolean;
  tradedPrice?: number;
  tradedTo?: string;
  security?: { id: number; cusip?: string; securityName?: string; referenceStatus?: string | null } | null;
}

interface MarketList {
  id: number;
  listType: 'BWIC' | 'OWIC' | 'RUN' | 'AXE';
  dealerName: string;
  listDate?: string;
  bidDeadline?: string | null;
  status?: string;
  totalFaceValue?: number;
  itemCount?: number;
  weParticipated?: boolean;
  ourWinsCount?: number;
  sourceChannel?: string;
  items?: MarketListItem[];
}

interface BidView {
  id: number;
  listId: number;
  itemId: number;
  state: 'DRAFT' | 'PRE_CLEARED' | 'SUBMITTED' | 'WON' | 'LOST' | 'DNT' | 'WITHDRAWN';
  bidPrice: number;
  bidSize: number;
  allocations: { portfolioId: number; par: number }[];
  complianceStatus?: 'PASS' | 'BREACH' | null;
  complianceDetail?: string;
  overrideId?: number;
  outcomePrice?: number;
  coverPrice?: number;
  ticketTradeId?: number;
  dealerName?: string;
  securityId?: number;
  cusip?: string;
  securityName?: string;
  securityDescription?: string;
  referenceStatus?: string | null;
  isLoan?: boolean;
}

interface SurvAlert {
  id: number;
  alertType: string;
  severity?: string;
  listId?: number | null;
  itemId?: number | null;
  securityId?: number | null;
  message: string;
}

interface PortfolioOption { id: number; portfolioName?: string; portfolioCode?: string }

interface CapacityInfo {
  portfolioId: number;
  maxPar: number;
  cappedAtUpperBound?: boolean;
  bindingConstraint?: string | null;
  openIntentPar?: number;
  overlapWarning?: string;
}

// ── Small building blocks ───────────────────────────────────────────────────

const fmtM = (v?: number | null) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const fmtPx = (v?: number | null) => (v == null ? '—' : Number(v).toFixed(2));

const deadlineLabel = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
};

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 ${className}`}>{children}</div>
);

const TypeChip: React.FC<{ type: string }> = ({ type }) => {
  const tone = type === 'BWIC' ? 'text-rose-300 bg-rose-500/10 border-rose-500/25'
    : type === 'OWIC' ? 'text-sky-300 bg-sky-500/10 border-sky-500/25'
    : 'text-slate-300 bg-white/5 border-white/10';
  return <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wide ${tone}`}>{type}</span>;
};

const Badge: React.FC<{ tone: 'sky' | 'amber' | 'emerald' | 'rose' | 'slate' | 'violet'; children: React.ReactNode; title?: string }> = ({ tone, children, title }) => {
  const tones: Record<string, string> = {
    sky: 'text-sky-300 bg-sky-500/10 border-sky-500/25',
    amber: 'text-amber-300 bg-amber-500/10 border-amber-500/25',
    emerald: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/25',
    rose: 'text-rose-300 bg-rose-500/10 border-rose-500/25',
    slate: 'text-slate-400 bg-white/5 border-white/10',
    violet: 'text-violet-300 bg-violet-500/10 border-violet-500/25',
  };
  return (
    <span title={title} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
};

/** The bid lifecycle rendered as a left-to-right chip rail. */
const StateRail: React.FC<{ bid: BidView }> = ({ bid }) => {
  const outcomeStates = ['WON', 'LOST', 'DNT', 'WITHDRAWN'];
  const reachedOutcome = outcomeStates.includes(bid.state);
  const steps = [
    { key: 'DRAFT', label: 'Staged', done: true },
    { key: 'PRE_CLEARED', label: bid.complianceStatus === 'BREACH' ? 'Breach' : 'Pre-cleared',
      done: bid.state !== 'DRAFT' || bid.complianceStatus === 'PASS',
      breach: bid.complianceStatus === 'BREACH' },
    { key: 'SUBMITTED', label: 'Submitted', done: bid.state === 'SUBMITTED' || reachedOutcome },
    { key: 'OUTCOME',
      label: reachedOutcome ? bid.state.charAt(0) + bid.state.slice(1).toLowerCase() : 'Outcome',
      done: reachedOutcome,
      final: bid.state },
  ];
  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => {
        let cls = 'text-slate-600 border-white/5 bg-transparent';
        if (s.done) cls = 'text-slate-300 border-white/10 bg-white/5';
        if (s.key === 'PRE_CLEARED' && (s as any).breach) cls = 'text-rose-300 border-rose-500/25 bg-rose-500/10';
        if (s.key === 'OUTCOME' && s.done) {
          cls = bid.state === 'WON' ? 'text-emerald-300 border-emerald-500/25 bg-emerald-500/10'
            : bid.state === 'LOST' ? 'text-rose-300 border-rose-500/25 bg-rose-500/10'
            : 'text-slate-300 border-white/10 bg-white/5';
        }
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <ChevronRight className="w-3 h-3 text-slate-700" />}
            <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${cls}`}>{s.label}</span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const DockButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'ghost' | 'emerald' | 'rose' }> = ({ tone = 'primary', className = '', children, ...rest }) => {
  const tones: Record<string, string> = {
    primary: 'bg-indigo-600 hover:bg-indigo-500 text-white border-transparent',
    emerald: 'bg-emerald-600/90 hover:bg-emerald-500 text-white border-transparent',
    rose: 'bg-rose-600/20 hover:bg-rose-600/30 text-rose-200 border-rose-500/30',
    ghost: 'bg-transparent hover:bg-white/5 text-slate-300 border-white/10',
  };
  return (
    <button {...rest} className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone]} ${className}`}>
      {children}
    </button>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] text-slate-400 mb-1">{label}</span>
    {children}
  </label>
);

const inputCls = 'w-full bg-slate-950/70 border border-white/10 rounded-lg px-2.5 py-1.5 text-[13px] text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500/60 tabular-nums';

// ── Main component ──────────────────────────────────────────────────────────

const LoanAuctionWorkbench: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [lists, setLists] = useState<MarketList[]>([]);
  const [bids, setBids] = useState<BidView[]>([]);
  const [alerts, setAlerts] = useState<SurvAlert[]>([]);
  const [portfolios, setPortfolios] = useState<PortfolioOption[]>([]);
  const [loadError, setLoadError] = useState(false);

  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [selectedBidId, setSelectedBidId] = useState<number | null>(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [actionError, setActionError] = useState('');

  // Stage form
  const [stagePrice, setStagePrice] = useState('');
  const [stageSize, setStageSize] = useState('');
  const [stagePortfolioId, setStagePortfolioId] = useState('');
  const [capacity, setCapacity] = useState<CapacityInfo | null>(null);
  const [capacityBusy, setCapacityBusy] = useState(false);

  // Bid panel inputs
  const [overrideId, setOverrideId] = useState('');
  const [outcomeMode, setOutcomeMode] = useState<'WON' | 'LOST' | null>(null);
  const [tradedPrice, setTradedPrice] = useState('');
  const [coverPrice, setCoverPrice] = useState('');
  const [linkTradeId, setLinkTradeId] = useState('');

  // Paste modal
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteDealer, setPasteDealer] = useState('');
  const [pasteDeadline, setPasteDeadline] = useState('');
  const [parsePreview, setParsePreview] = useState<any | null>(null);
  const [parseBusy, setParseBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    const [listsRes, bidsRes, alertsRes, pfRes] = await Promise.all([
      axios.get('/api/credit/secondary/active').catch(() => null),
      axios.get('/api/credit/bwic/bids').catch(() => null),
      axios.get('/api/credit/bwic/alerts?unackedOnly=true').catch(() => null),
      axios.get('/api/dimensions/portfolios').catch(() => null),
    ]);
    setLoadError(!listsRes || !bidsRes);
    if (listsRes?.data) setLists(Array.isArray(listsRes.data) ? listsRes.data : []);
    if (bidsRes?.data) setBids(Array.isArray(bidsRes.data) ? bidsRes.data : []);
    if (alertsRes?.data) setAlerts(Array.isArray(alertsRes.data) ? alertsRes.data : []);
    if (pfRes?.data) {
      const p = Array.isArray(pfRes.data) ? pfRes.data : pfRes.data?.content || [];
      setPortfolios(p);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Deep-link support: ?list=27&item=39
  useEffect(() => {
    const l = Number(searchParams.get('list'));
    if (l && lists.some(x => x.id === l)) setSelectedListId(l);
    const it = Number(searchParams.get('item'));
    if (it) setSelectedItemId(it);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists.length]);

  useEffect(() => {
    if (selectedListId == null && lists.length > 0) setSelectedListId(lists[0].id);
  }, [lists, selectedListId]);

  const selectedList = useMemo(() => lists.find(l => l.id === selectedListId) || null, [lists, selectedListId]);
  const selectedItem = useMemo(
    () => selectedList?.items?.find(i => i.id === selectedItemId) || null,
    [selectedList, selectedItemId],
  );
  const selectedBid = useMemo(() => bids.find(b => b.id === selectedBidId) || null, [bids, selectedBidId]);
  const listBids = useMemo(() => bids.filter(b => b.listId === selectedListId), [bids, selectedListId]);

  const alertsByItem = useMemo(() => {
    const m = new Map<number, SurvAlert[]>();
    alerts.forEach(a => { if (a.itemId != null) m.set(a.itemId, [...(m.get(a.itemId) || []), a]); });
    return m;
  }, [alerts]);

  const stats = useMemo(() => {
    const wins = bids.filter(b => b.state === 'WON').length;
    const decided = bids.filter(b => ['WON', 'LOST', 'DNT'].includes(b.state)).length;
    return {
      active: lists.filter(l => l.status !== 'CLOSED').length,
      participated: lists.filter(l => l.weParticipated).length,
      wins,
      hitRate: decided > 0 ? Math.round((wins / decided) * 100) : null,
      dealers: new Set(lists.map(l => l.dealerName)).size,
    };
  }, [lists, bids]);

  const err = (e: any) => setActionError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Action failed');

  // ── Selection handlers ───────────────────────────────────────────────────

  const selectItem = (item: MarketListItem) => {
    setSelectedItemId(item.id);
    setSelectedBidId(null);
    setActionError('');
    setCapacity(null);
    setStagePrice(item.talkPriceLow != null ? String(item.talkPriceLow) : '');
    setStageSize(item.faceAmount != null ? String(item.faceAmount) : '');
  };

  const selectBid = (bid: BidView) => {
    setSelectedBidId(bid.id);
    setSelectedItemId(null);
    setActionError('');
    setOverrideId(bid.overrideId ? String(bid.overrideId) : '');
    setOutcomeMode(null);
    setTradedPrice('');
    setCoverPrice('');
    setLinkTradeId('');
  };

  // ── Capacity (debounced, FR-4) ───────────────────────────────────────────

  useEffect(() => {
    if (!selectedItem || !selectedList || !stagePortfolioId || !stagePrice) { setCapacity(null); return; }
    const t = setTimeout(async () => {
      setCapacityBusy(true);
      try {
        const res = await axios.post('/api/credit/bwic/capacity', {
          listId: selectedList.id,
          itemId: selectedItem.id,
          portfolioIds: [Number(stagePortfolioId)],
          price: parseFloat(stagePrice),
          side: selectedList.listType === 'OWIC' ? 'SELL' : 'BUY',
          upperBound: selectedItem.faceAmount || undefined,
        });
        setCapacity(Array.isArray(res.data) && res.data.length ? res.data[0] : null);
      } catch { setCapacity(null); } finally { setCapacityBusy(false); }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, stagePortfolioId, stagePrice]);

  // ── Actions (the governed machine — unchanged endpoints) ─────────────────

  const stageBid = async () => {
    if (!selectedList || !selectedItem) return;
    setActionError('');
    try {
      const res = await axios.post(`/api/credit/bwic/lists/${selectedList.id}/items/${selectedItem.id}/bids`, {
        bidPrice: parseFloat(stagePrice),
        bidSize: parseFloat(stageSize),
        allocations: [{ portfolioId: Number(stagePortfolioId), par: parseFloat(stageSize) }],
      });
      await fetchAll();
      if (res.data?.id) { setSelectedBidId(res.data.id); setSelectedItemId(null); }
    } catch (e) { err(e); }
  };

  const preclear = async (bidId: number) => {
    setActionError('');
    try { await axios.post(`/api/credit/bwic/bids/${bidId}/preclear`); await fetchAll(); } catch (e) { err(e); }
  };

  const submit = async (bid: BidView) => {
    setActionError('');
    try {
      await axios.post(`/api/credit/bwic/bids/${bid.id}/submit`,
        bid.complianceStatus === 'BREACH' ? { overrideId: Number(overrideId) } : {});
      await fetchAll();
    } catch (e) { err(e); }
  };

  const recordOutcome = async (bidId: number, outcome: string) => {
    setActionError('');
    try {
      await axios.post(`/api/credit/bwic/bids/${bidId}/outcome`, {
        outcome,
        tradedPrice: outcome === 'WON' && tradedPrice ? parseFloat(tradedPrice) : undefined,
        coverPrice: coverPrice ? parseFloat(coverPrice) : undefined,
      });
      setOutcomeMode(null); setTradedPrice(''); setCoverPrice('');
      await fetchAll();
    } catch (e) { err(e); }
  };

  const linkTrade = async (bidId: number) => {
    setActionError('');
    try {
      await axios.post(`/api/credit/bwic/bids/${bidId}/link-trade`, { tradeId: Number(linkTradeId) });
      setLinkTradeId('');
      await fetchAll();
    } catch (e) { err(e); }
  };

  const ackAlert = async (id: number) => {
    try { await axios.post(`/api/credit/bwic/alerts/${id}/ack`); setAlerts(a => a.filter(x => x.id !== id)); } catch { /* keep row */ }
  };

  const ackAllForList = async (listId: number | null) => {
    const target = alerts.filter(a => (a.listId ?? null) === listId);
    for (const a of target) { // sequential, small N
      // eslint-disable-next-line no-await-in-loop
      await axios.post(`/api/credit/bwic/alerts/${a.id}/ack`).catch(() => null);
    }
    setAlerts(a => a.filter(x => (x.listId ?? null) !== listId));
  };

  const closeList = async (listId: number) => {
    setActionError('');
    try { await axios.post(`/api/credit/bwic/lists/${listId}/close`); await fetchAll(); } catch (e) { err(e); }
  };

  // FR-6.1: a win opens the governed ticket prefilled — never an auto-book.
  const openTicket = (bid: BidView) => {
    const route = bid.isLoan ? '/trade-entry/leveraged-loan' : '/trade-entry/fixed-income';
    navigate(route, {
      state: {
        ticketPrefill: {
          securityId: bid.securityId,
          cusip: bid.cusip,
          security: bid.securityName || bid.securityDescription,
          parAmount: bid.bidSize,
          cleanPrice: bid.outcomePrice ?? bid.bidPrice,
          counterparty: bid.dealerName,
          channel: 'SECONDARY',
          sourceBidIntentId: bid.id,
        },
      },
    });
  };

  // ── Paste-to-parse (FR-2) ────────────────────────────────────────────────

  const parseList = async () => {
    setParseBusy(true); setActionError('');
    try {
      const res = await axios.post('/api/credit/bwic/parse-list', { rawText: pasteText });
      setParsePreview(res.data);
      if (res.data?.dealerName) setPasteDealer(res.data.dealerName);
      // F-BWE2E-001: surface the full deadline for correction; default the
      // date to TODAY when the parser returned a date-less/implausible one.
      if (res.data?.bidDeadline) {
        const d = new Date(res.data.bidDeadline);
        const now = new Date();
        if (!Number.isNaN(d.getTime())) {
          const fixed = d < now ? new Date(`${now.toISOString().slice(0, 10)}T${res.data.bidDeadline.slice(11, 16) || '11:00'}`) : d;
          setPasteDeadline(`${fixed.toISOString().slice(0, 10)}T${fixed.toTimeString().slice(0, 5)}`);
        }
      } else {
        setPasteDeadline('');
      }
    } catch (e) { err(e); } finally { setParseBusy(false); }
  };

  const confirmParsedList = async () => {
    if (!parsePreview) return;
    const items = (parsePreview.items || []).filter((i: any) => i.securityDescription);
    setActionError('');
    try {
      const res = await axios.post('/api/credit/secondary/lists', {
        listType: parsePreview.listType || 'BWIC',
        dealerName: pasteDealer || 'Unknown Dealer',
        bidDeadline: pasteDeadline ? `${pasteDeadline}:00` : null,
        listDate: getTenantToday(),
        status: 'ACTIVE',
        sourceChannel: 'PASTE_PARSE',
        rawSourceText: pasteText,
        totalFaceValue: items.reduce((s: number, i: any) => s + (Number(i.faceAmount) || 0), 0),
        itemCount: items.length,
        weParticipated: false,
        items: items.map((i: any) => ({
          securityDescription: i.securityDescription,
          securityIdentifier: i.securityIdentifier || null,
          faceAmount: Number(i.faceAmount) || 0,
          talkPriceLow: i.talkPriceLow != null ? Number(i.talkPriceLow) : null,
          talkPriceHigh: i.talkPriceHigh != null ? Number(i.talkPriceHigh) : null,
          rawSourceLine: i.rawLine || null,
        })),
      });
      setPasteOpen(false); setPasteText(''); setParsePreview(null); setPasteDealer(''); setPasteDeadline('');
      await fetchAll();
      if (res.data?.id) setSelectedListId(res.data.id);
    } catch (e) { err(e); }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const dockMode: 'bid' | 'stage' | 'empty' = selectedBid ? 'bid' : selectedItem ? 'stage' : 'empty';
  const alertGroups = useMemo(() => {
    const groups = new Map<number | null, SurvAlert[]>();
    alerts.forEach(a => {
      const k = a.listId ?? null;
      groups.set(k, [...(groups.get(k) || []), a]);
    });
    return groups;
  }, [alerts]);

  const alertTone = (t: string): 'sky' | 'amber' | 'violet' | 'rose' =>
    t === 'HOLDINGS_HIT' ? 'sky' : t === 'ISSUER_OVERLAP' ? 'amber' : t === 'COLOR_MARK_MOVE' ? 'violet' : 'rose';

  return (
    <div className="text-slate-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <Eyebrow>Construction · Leveraged Loans</Eyebrow>
          <h2 className="text-xl font-semibold text-white tracking-tight flex items-center gap-2 mt-0.5">
            <Gavel className="w-5 h-5 text-indigo-400" /> Loan Auctions — BWIC / OWIC
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Bid-time construction with live capacity and compliance. Wins convert only through the governed LL ticket.
          </p>
        </div>
        <div className="flex items-center gap-2 relative">
          <button
            onClick={() => setInboxOpen(o => !o)}
            className="relative p-2 rounded-lg border border-white/10 bg-slate-900/60 hover:bg-white/5 transition-colors"
            title="Surveillance alerts"
          >
            <Bell className="w-4 h-4 text-slate-300" />
            {alerts.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                {alerts.length}
              </span>
            )}
          </button>
          <DockButton onClick={() => { setPasteOpen(true); setParsePreview(null); }}>
            <span className="inline-flex items-center gap-1.5"><ClipboardPaste className="w-3.5 h-3.5" /> Paste List</span>
          </DockButton>

          {/* Alert inbox (Decision 3) */}
          {inboxOpen && (
            <div className="absolute right-0 top-11 z-40 w-[460px] max-h-[480px] overflow-y-auto rounded-xl border border-white/10 bg-slate-900 shadow-2xl shadow-black/50">
              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                <Eyebrow>Market surveillance — {alerts.length} unacknowledged</Eyebrow>
                <button onClick={() => setInboxOpen(false)}><X className="w-3.5 h-3.5 text-slate-500 hover:text-white" /></button>
              </div>
              {alerts.length === 0 && (
                <div className="px-4 py-6 text-xs text-slate-500">Nothing unacknowledged — the tape is quiet.</div>
              )}
              {Array.from(alertGroups.entries()).map(([listId, group]) => {
                const list = lists.find(l => l.id === listId);
                return (
                  <div key={String(listId)} className="border-b border-white/5 last:border-0">
                    <div className="px-4 pt-2.5 pb-1 flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-300">
                        {list ? `${list.dealerName} · ${list.listType}` : 'Book-wide'}
                        <span className="text-slate-600"> · {group.length}</span>
                      </span>
                      <button onClick={() => ackAllForList(listId)} className="text-[10px] text-slate-500 hover:text-slate-300">Ack all</button>
                    </div>
                    {group.map((a: SurvAlert) => (
                      <div key={a.id} className="px-4 py-1.5 flex items-start gap-2 hover:bg-white/[0.03]">
                        <Badge tone={alertTone(a.alertType)}>{a.alertType.replace(/_/g, ' ')}</Badge>
                        <span className="flex-1 text-[11px] text-slate-400 leading-snug">{a.message}</span>
                        <button onClick={() => ackAlert(a.id)} className="text-[10px] text-slate-600 hover:text-slate-300 shrink-0 mt-0.5">Ack</button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {loadError && (
        <div className="mb-3 px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-300 text-xs">
          Some auction data failed to load — this is a load error, not an empty tape. Refresh to retry.
        </div>
      )}
      {actionError && (
        <div className="mb-3 px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-300 text-xs flex items-center justify-between">
          <span>{actionError}</span>
          <button onClick={() => setActionError('')}><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Three zones */}
      <div className="grid grid-cols-[300px_minmax(0,1fr)_360px] gap-4 items-start">

        {/* ── Rail ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          <div className="rounded-xl border border-white/5 bg-slate-900/50 overflow-hidden">
            <div className="px-3 py-2.5 border-b border-white/5"><Eyebrow>Auctions</Eyebrow></div>
            <div className="max-h-[52vh] overflow-y-auto">
              {lists.length === 0 && <div className="px-3 py-5 text-xs text-slate-500">No lists on the tape. Paste one to begin.</div>}
              {lists.map(l => {
                const active = l.id === selectedListId;
                const dl = deadlineLabel(l.bidDeadline);
                return (
                  <button
                    key={l.id}
                    onClick={() => { setSelectedListId(l.id); setSelectedItemId(null); setSelectedBidId(null); }}
                    className={`w-full text-left px-3 py-2.5 border-b border-white/[0.03] last:border-0 transition-colors ${active ? 'bg-indigo-500/10 border-l-2 border-l-indigo-500' : 'hover:bg-white/[0.03] border-l-2 border-l-transparent'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-medium text-white truncate">{l.dealerName}</span>
                      <TypeChip type={l.listType} />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[11px] text-slate-500 tabular-nums">{l.itemCount ?? l.items?.length ?? 0} lines · {fmtM(l.totalFaceValue)}</span>
                      <span className="text-[11px] text-slate-500 tabular-nums">{dl ? `due ${dl}` : ''}</span>
                    </div>
                    {l.weParticipated && (
                      <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-400/80">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> participating{(l.ourWinsCount || 0) > 0 ? ` · ${l.ourWinsCount} won` : ''}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Auction stats band (Decision 5) */}
          <div className="rounded-xl border border-white/5 bg-slate-900/50 px-3 py-2.5">
            <Eyebrow className="mb-2">Auction stats</Eyebrow>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {[
                ['Active lists', stats.active],
                ['Participated', stats.participated],
                ['Wins', stats.wins],
                ['Hit rate', stats.hitRate == null ? '—' : `${stats.hitRate}%`],
              ].map(([k, v]) => (
                <div key={String(k)}>
                  <div className="text-[10px] text-slate-500">{k}</div>
                  <div className="text-sm font-semibold text-white tabular-nums">{v as any}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Detail ───────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-white/5 bg-slate-900/50 overflow-hidden min-h-[420px]">
          {!selectedList ? (
            <div className="p-8 text-sm text-slate-500">Select an auction from the rail.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold text-white">{selectedList.dealerName}</span>
                    <TypeChip type={selectedList.listType} />
                    {selectedList.sourceChannel && <Badge tone="slate">{selectedList.sourceChannel.replace(/_/g, ' ')}</Badge>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
                    {selectedList.itemCount ?? selectedList.items?.length ?? 0} lines · {fmtM(selectedList.totalFaceValue)}
                    {deadlineLabel(selectedList.bidDeadline) ? ` · bids due ${deadlineLabel(selectedList.bidDeadline)}` : ''}
                  </div>
                </div>
                {selectedList.status !== 'CLOSED' ? (
                  <DockButton tone="ghost" onClick={() => closeList(selectedList.id)}>Close list</DockButton>
                ) : <Badge tone="slate">CLOSED</Badge>}
              </div>

              {/* Line items */}
              <div className="divide-y divide-white/[0.03]">
                {(selectedList.items || []).map(item => {
                  const itemAlerts = alertsByItem.get(item.id) || [];
                  const stub = item.resolutionStatus === 'STUB_CREATED' || item.security?.referenceStatus === 'UNVERIFIED_STUB';
                  const held = itemAlerts.some(a => a.alertType === 'HOLDINGS_HIT');
                  const active = item.id === selectedItemId;
                  return (
                    <div
                      key={item.id}
                      className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer transition-colors ${active ? 'bg-indigo-500/10' : 'hover:bg-white/[0.03]'}`}
                      onClick={() => selectItem(item)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-medium text-white truncate">{item.security?.securityName || item.securityDescription}</span>
                          {held && <Badge tone="sky" title={itemAlerts.find(a => a.alertType === 'HOLDINGS_HIT')?.message}>HELD</Badge>}
                          {stub && <Badge tone="amber" title="Unverified reference stub — promote via Reference Data before booking a win">STUB</Badge>}
                          {itemAlerts.filter(a => a.alertType !== 'HOLDINGS_HIT').map(a => (
                            <Badge key={a.id} tone={alertTone(a.alertType)} title={a.message}>{a.alertType === 'ISSUER_OVERLAP' ? 'ISSUER' : a.alertType.split('_')[0]}</Badge>
                          ))}
                        </div>
                        <div className="text-[11px] text-slate-500 tabular-nums mt-0.5">
                          {item.securityIdentifier || item.security?.cusip || '—'} · face {fmtM(item.faceAmount)} · talk {fmtPx(item.talkPriceLow)}{item.talkPriceHigh != null ? `–${fmtPx(item.talkPriceHigh)}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {item.weWon && <Badge tone="emerald" title={item.tradedTo ? `traded to ${item.tradedTo}` : undefined}>WON {fmtPx(item.tradedPrice)}</Badge>}
                        {!item.weWon && item.ourBidPrice != null && <Badge tone="slate">bid {fmtPx(item.ourBidPrice)}</Badge>}
                        <DockButton tone={active ? 'primary' : 'ghost'} onClick={(e) => { e.stopPropagation(); selectItem(item); }}>
                          Construct bid
                        </DockButton>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Our bids on this list */}
              <div className="border-t border-white/5">
                <div className="px-4 pt-3 pb-1"><Eyebrow>Our bids on this list</Eyebrow></div>
                {listBids.length === 0 && <div className="px-4 pb-4 pt-1 text-xs text-slate-500">None yet — select a line and construct one.</div>}
                {listBids.map(b => {
                  const active = b.id === selectedBidId;
                  return (
                    <div
                      key={b.id}
                      onClick={() => selectBid(b)}
                      className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer border-t border-white/[0.03] transition-colors ${active ? 'bg-indigo-500/10' : 'hover:bg-white/[0.03]'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium text-white truncate">
                          {b.securityName || b.securityDescription}
                          <span className="text-slate-500 font-normal tabular-nums"> · {fmtM(b.bidSize)} @ {fmtPx(b.bidPrice)}</span>
                        </div>
                        <div className="mt-1"><StateRail bid={b} /></div>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        {b.state === 'WON' && !b.ticketTradeId && <Badge tone="amber">UNBOOKED</Badge>}
                        {b.ticketTradeId && <Badge tone="emerald">#{b.ticketTradeId}</Badge>}
                        <ChevronRight className="w-4 h-4 text-slate-600" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* ── Action dock ──────────────────────────────────────────────── */}
        <div className="rounded-xl border border-white/5 bg-slate-900/50 overflow-hidden sticky top-4">
          {dockMode === 'empty' && (
            <div className="p-6 text-center">
              <Landmark className="w-8 h-8 text-slate-700 mx-auto mb-3" />
              <div className="text-sm text-slate-400 font-medium">Nothing selected</div>
              <div className="text-xs text-slate-600 mt-1">Pick a line to construct a bid, or one of your bids to advance it.</div>
            </div>
          )}

          {dockMode === 'stage' && selectedItem && selectedList && (
            <div className="p-4">
              <Eyebrow className="mb-1">Stage bid — {selectedList.listType === 'OWIC' ? 'sell' : 'buy'}</Eyebrow>
              <div className="text-sm font-semibold text-white mb-3">{selectedItem.security?.securityName || selectedItem.securityDescription}</div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Bid price">
                    <input className={inputCls} type="number" step="0.01" value={stagePrice} onChange={e => setStagePrice(e.target.value)} placeholder="98.50" />
                  </Field>
                  <Field label="Size / par ($)">
                    <input className={inputCls} type="number" value={stageSize} onChange={e => setStageSize(e.target.value)} placeholder="1000000" />
                  </Field>
                </div>
                <Field label="Portfolio (allocation)">
                  <select className={inputCls} value={stagePortfolioId} onChange={e => setStagePortfolioId(e.target.value)}>
                    <option value="">Select portfolio</option>
                    {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName || p.portfolioCode || `Portfolio ${p.id}`}</option>)}
                  </select>
                  {portfolios.length === 0 && <div className="text-[10px] text-rose-300 mt-1">Portfolio list failed to load — refresh.</div>}
                </Field>

                {/* Capacity readout (FR-4) */}
                {(capacityBusy || capacity) && (
                  <div className="rounded-lg border border-indigo-500/25 bg-indigo-500/[0.07] px-3 py-2 text-[11px]">
                    {capacityBusy ? (
                      <span className="text-slate-500">Computing capacity…</span>
                    ) : capacity && (
                      <>
                        <div className="text-indigo-200 font-semibold tabular-nums">Max bid size: {fmtM(capacity.maxPar)}</div>
                        {capacity.bindingConstraint && <div className="text-slate-400 mt-0.5 italic">{capacity.bindingConstraint}</div>}
                        {capacity.overlapWarning && <div className="text-amber-300 mt-0.5">{capacity.overlapWarning}</div>}
                        {stageSize && capacity.maxPar != null && parseFloat(stageSize) > Number(capacity.maxPar) && (
                          <div className="text-amber-300 mt-0.5 font-semibold">Your size exceeds capacity — pre-clear will breach.</div>
                        )}
                        {(selectedItem.resolutionStatus === 'STUB_CREATED' || selectedItem.security?.referenceStatus === 'UNVERIFIED_STUB') && (
                          <div className="text-amber-300/90 mt-0.5">Unverified reference — concentration rules cannot fully evaluate this line.</div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <DockButton tone="ghost" onClick={() => setSelectedItemId(null)}>Cancel</DockButton>
                  <DockButton onClick={stageBid} disabled={!stagePrice || !stageSize || !stagePortfolioId}>Stage (DRAFT)</DockButton>
                </div>
              </div>
            </div>
          )}

          {dockMode === 'bid' && selectedBid && (
            <div className="p-4">
              <Eyebrow className="mb-1">Bid #{selectedBid.id}</Eyebrow>
              <div className="text-sm font-semibold text-white">{selectedBid.securityName || selectedBid.securityDescription}</div>
              <div className="text-[11px] text-slate-500 tabular-nums mb-2">{fmtM(selectedBid.bidSize)} @ {fmtPx(selectedBid.bidPrice)} · {selectedBid.dealerName}</div>
              <div className="mb-3"><StateRail bid={selectedBid} /></div>

              {/* Compliance verdict (parsed rule list) */}
              {selectedBid.complianceDetail && (() => {
                let rows: any[] = [];
                try { rows = JSON.parse(selectedBid.complianceDetail); } catch { /* raw */ }
                return rows.length > 0 && (
                  <div className={`rounded-lg border px-3 py-2 mb-3 text-[11px] ${selectedBid.complianceStatus === 'BREACH' ? 'border-rose-500/25 bg-rose-500/[0.07]' : 'border-emerald-500/25 bg-emerald-500/[0.07]'}`}>
                    <div className={`font-semibold mb-1 flex items-center gap-1.5 ${selectedBid.complianceStatus === 'BREACH' ? 'text-rose-300' : 'text-emerald-300'}`}>
                      {selectedBid.complianceStatus === 'BREACH' ? <ShieldAlert className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      {selectedBid.complianceStatus === 'BREACH' ? 'Compliance breach' : 'Compliance pass'}
                    </div>
                    {rows.map((r: any, i: number) => (
                      <div key={i} className="text-slate-400">
                        <span className="text-slate-500">pf {r.portfolioId} · {fmtM(r.par)}:</span>{' '}
                        {r.compliant ? 'clear' : (r.errors || []).join('; ')}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Next action by state */}
              {selectedBid.state === 'DRAFT' && selectedBid.complianceStatus !== 'BREACH' && (
                <div className="flex justify-end gap-2">
                  <DockButton tone="ghost" onClick={() => recordOutcome(selectedBid.id, 'WITHDRAWN')}>Withdraw</DockButton>
                  <DockButton onClick={() => preclear(selectedBid.id)}>Pre-clear</DockButton>
                </div>
              )}

              {selectedBid.state === 'DRAFT' && selectedBid.complianceStatus === 'BREACH' && (
                <div className="space-y-2">
                  <Field label="Approved compliance override #">
                    <input className={inputCls} value={overrideId} onChange={e => setOverrideId(e.target.value)} placeholder="e.g. 33" />
                  </Field>
                  <div className="text-[10px] text-slate-500">A breaching bid submits only under a CCO-approved override. Request one via Compliance.</div>
                  <div className="flex justify-end gap-2">
                    <DockButton tone="ghost" onClick={() => recordOutcome(selectedBid.id, 'WITHDRAWN')}>Withdraw</DockButton>
                    <DockButton onClick={() => submit(selectedBid)} disabled={!overrideId}>Submit w/ override</DockButton>
                  </div>
                </div>
              )}

              {selectedBid.state === 'PRE_CLEARED' && (
                <div className="flex justify-end gap-2">
                  <DockButton tone="ghost" onClick={() => recordOutcome(selectedBid.id, 'WITHDRAWN')}>Withdraw</DockButton>
                  <DockButton onClick={() => submit(selectedBid)}>Submit to dealer</DockButton>
                </div>
              )}

              {selectedBid.state === 'SUBMITTED' && !outcomeMode && (
                <div className="space-y-2">
                  <Eyebrow>Record auction result</Eyebrow>
                  <div className="flex gap-2">
                    <DockButton tone="emerald" onClick={() => setOutcomeMode('WON')}>Won</DockButton>
                    <DockButton tone="rose" onClick={() => setOutcomeMode('LOST')}>Lost</DockButton>
                    <DockButton tone="ghost" onClick={() => recordOutcome(selectedBid.id, 'DNT')}>DNT</DockButton>
                    <DockButton tone="ghost" onClick={() => recordOutcome(selectedBid.id, 'WITHDRAWN')}>Withdraw</DockButton>
                  </div>
                </div>
              )}

              {selectedBid.state === 'SUBMITTED' && outcomeMode && (
                <div className="space-y-2">
                  <Eyebrow>{outcomeMode === 'WON' ? 'Record win' : 'Record loss'}</Eyebrow>
                  {outcomeMode === 'WON' && (
                    <Field label="Traded price (evidenced)">
                      <input className={inputCls} type="number" step="0.01" autoFocus value={tradedPrice} onChange={e => setTradedPrice(e.target.value)} placeholder="98.50" />
                    </Field>
                  )}
                  <Field label="Cover price (if disseminated)">
                    <input className={inputCls} type="number" step="0.01" value={coverPrice} onChange={e => setCoverPrice(e.target.value)} placeholder="98.30" />
                  </Field>
                  <div className="flex justify-end gap-2">
                    <DockButton tone="ghost" onClick={() => setOutcomeMode(null)}>Back</DockButton>
                    <DockButton tone={outcomeMode === 'WON' ? 'emerald' : 'rose'} onClick={() => recordOutcome(selectedBid.id, outcomeMode)} disabled={outcomeMode === 'WON' && !tradedPrice}>
                      Record {outcomeMode}
                    </DockButton>
                  </div>
                </div>
              )}

              {selectedBid.state === 'WON' && (
                <div className="space-y-3">
                  {!selectedBid.ticketTradeId ? (
                    <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2.5">
                      <div className="text-amber-300 text-[11px] font-semibold mb-1">Won @ {fmtPx(selectedBid.outcomePrice)} — not yet booked</div>
                      <div className="text-[10px] text-slate-500 mb-2">
                        {selectedBid.referenceStatus === 'UNVERIFIED_STUB'
                          ? 'Unverified reference stub — promote via Reference Data (define the facility with this identifier) before booking.'
                          : 'Convert through the governed LL ticket — the win never books itself.'}
                      </div>
                      <div className="flex items-center gap-2">
                        <DockButton
                          onClick={() => openTicket(selectedBid)}
                          disabled={selectedBid.referenceStatus === 'UNVERIFIED_STUB'}
                          title={selectedBid.referenceStatus === 'UNVERIFIED_STUB' ? 'Unverified reference stub — promote via Reference Data or document ingestion before booking' : undefined}
                        >
                          <span className="inline-flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5" /> Open ticket</span>
                        </DockButton>
                        <input className={`${inputCls} w-24`} placeholder="Trade #" value={linkTradeId} onChange={e => setLinkTradeId(e.target.value)} />
                        <DockButton tone="ghost" onClick={() => linkTrade(selectedBid.id)} disabled={!linkTradeId}>
                          <span className="inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> Link</span>
                        </DockButton>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.07] px-3 py-2.5">
                      <div className="text-emerald-300 text-[11px] font-semibold flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Booked — trade #{selectedBid.ticketTradeId}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">Settlement runs in Middle Office → Loan Settlement.</div>
                      <div className="mt-2">
                        <DockButton tone="ghost" onClick={() => navigate('/middle-office?tab=loan-settlement')}>Open settlement queue →</DockButton>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {['LOST', 'DNT', 'WITHDRAWN'].includes(selectedBid.state) && (
                <div className="text-[11px] text-slate-500">
                  Terminal — {selectedBid.state === 'LOST' ? `lost${selectedBid.coverPrice != null ? ` (cover ${fmtPx(selectedBid.coverPrice)})` : ''}; retained as market color.` : selectedBid.state === 'DNT' ? 'did not trade; retained as market color.' : 'withdrawn before the auction closed.'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Paste-to-parse modal (FR-2) */}
      {pasteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setPasteOpen(false)}>
          <div className="w-full max-w-2xl rounded-xl border border-white/10 bg-slate-900 shadow-2xl p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <Eyebrow>Paste dealer list</Eyebrow>
                <div className="text-sm font-semibold text-white">Email or spreadsheet text → structured preview → governed create</div>
              </div>
              <button onClick={() => setPasteOpen(false)}><X className="w-4 h-4 text-slate-500 hover:text-white" /></button>
            </div>

            {!parsePreview ? (
              <>
                <textarea
                  className={`${inputCls} h-40 resize-vertical font-mono text-[12px]`}
                  placeholder={'BWIC - BIDS DUE 11:00 AM\nMEDLINE TLB 58401DAB7 3,000,000 talk 98.25\n…'}
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                />
                <div className="flex justify-end gap-2 mt-3">
                  <DockButton tone="ghost" onClick={() => setPasteOpen(false)}>Cancel</DockButton>
                  <DockButton onClick={parseList} disabled={!pasteText.trim() || parseBusy}>{parseBusy ? 'Parsing…' : 'Parse'}</DockButton>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <Field label="Dealer">
                    <input className={inputCls} value={pasteDealer} onChange={e => setPasteDealer(e.target.value)} />
                  </Field>
                  <Field label="Type">
                    <input className={inputCls} value={parsePreview.listType || 'BWIC'} readOnly />
                  </Field>
                  <Field label="Bids due (verify the date)">
                    <input className={inputCls} type="datetime-local" value={pasteDeadline} onChange={e => setPasteDeadline(e.target.value)} />
                  </Field>
                </div>
                <div className="rounded-lg border border-white/5 overflow-hidden mb-3">
                  <table className="w-full text-[12px]">
                    <thead className="bg-slate-950/60 text-slate-500">
                      <tr>
                        <th className="text-left px-2.5 py-1.5 font-semibold">Description</th>
                        <th className="text-left px-2.5 py-1.5 font-semibold">Identifier</th>
                        <th className="text-right px-2.5 py-1.5 font-semibold">Face</th>
                        <th className="text-right px-2.5 py-1.5 font-semibold">Talk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(parsePreview.items || []).map((i: any, idx: number) => (
                        <tr key={idx} className="border-t border-white/[0.03] text-slate-300">
                          <td className="px-2.5 py-1.5">{i.securityDescription}</td>
                          <td className="px-2.5 py-1.5 text-slate-500">{i.securityIdentifier || '—'}</td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums">{fmtM(Number(i.faceAmount) || null)}</td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums">{fmtPx(i.talkPriceLow)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(parsePreview.warnings || []).length > 0 && (
                  <div className="text-[11px] text-amber-300 mb-3">{(parsePreview.warnings || []).join(' · ')}</div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-600">Parser: {parsePreview.parserUsed}. Nothing is saved until you confirm.</span>
                  <div className="flex gap-2">
                    <DockButton tone="ghost" onClick={() => setParsePreview(null)}>Back</DockButton>
                    <DockButton onClick={confirmParsedList} disabled={!pasteDealer.trim() || !(parsePreview.items || []).length}>Confirm & create</DockButton>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanAuctionWorkbench;
